import { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import api from '../../api/client';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useRealtime } from '../../hooks/useRealtime.js';
import { emitTyping } from '../../api/collab.js';
import { dmsApi } from '../../api/dms.js';
import MentionTextarea from './MentionTextarea.jsx';
import MessageBody from './MessageBody.jsx';

/**
 * BOS+ Phase 14a/14b — reusable entity-attached discussion thread.
 *
 * Drops onto any business object: give it the subject coordinates and it
 * get-or-creates the thread, lists its NEWEST page of messages, then subscribes
 * to the `thread:<id>` realtime room and appends live `message.new` events
 * (deduped by message id, so an optimistic local echo and the server broadcast
 * collapse into one row). "Load older" walks backward via the `before` cursor.
 *
 * 14b: the composer is a real @mention picker (structured `@[Name](user:uuid)`
 * tokens), bodies render mentions as chips, and the view stays pinned to the
 * newest message unless the user has scrolled up to read history.
 */

const unwrap = (r) => r.data?.data;
const GROUP_WINDOW_MS = 5 * 60 * 1000; // collapse consecutive same-sender posts within 5 min
const PAGE_SIZE = 50;
const COMMON_EMOJI = ['👍', '❤️', '😄', '🎉', '👀', '✅'];

// Append a message unless one with the same id is already present. Used both by
// the optimistic send path and the realtime echo so they never double up.
function appendUnique(prev, msg) {
  return prev.some((m) => m.id === msg.id) ? prev : [...prev, msg];
}

// Total order matching the server: (created_at, id). Random-UUID PKs are not
// monotonic, so id alone is not a valid order.
function byCreatedAtId(a, b) {
  const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
  const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
  if (ta !== tb) return ta - tb;
  return String(a.id).localeCompare(String(b.id));
}

function initial(name) {
  return (name || '?').trim().charAt(0).toUpperCase() || '?';
}

// Only allow http(s) or app-relative URLs for a shared-object link — the entity
// url is user-supplied (via the message API), so block javascript:/data: etc.
function safeHref(u) {
  return typeof u === 'string' && /^(https?:\/\/|\/)/.test(u) ? u : '#';
}

function timeLabel(d) {
  return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
}

// "Today" / "Yesterday" / weekday (this week) / date — for the day dividers.
function dayLabel(d) {
  if (!d) return 'Earlier';
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const now = new Date();
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays > 1 && diffDays < 7) return d.toLocaleDateString([], { weekday: 'long' });
  return d.toLocaleDateString([], {
    month: 'short', day: 'numeric',
    year: now.getFullYear() === d.getFullYear() ? undefined : 'numeric',
  });
}

// Turn an ALREADY-SORTED message list into render items: day dividers, plus
// per-message flags for "mine" (self) and "grouped" (collapse avatar/header for
// a run of same-sender posts within GROUP_WINDOW_MS). A day change breaks a run.
function decorate(ordered, myId) {
  const out = [];
  let lastDayKey = null;
  let lastSender;
  let lastTime = null;
  for (const m of ordered) {
    const d = m.created_at ? new Date(m.created_at) : null;
    const dayKey = d ? d.toDateString() : 'unknown';
    if (dayKey !== lastDayKey) {
      // Key on the first message under the divider — unique even if a day
      // recurs, so React never sees duplicate keys (a console error).
      out.push({ kind: 'day', key: `day-${m.id}`, label: dayLabel(d) });
      lastDayKey = dayKey;
      lastSender = undefined;
      lastTime = null;
    }
    const delta = lastTime && d ? d - lastTime : Infinity;
    const grouped = !!(m.sender_id && m.sender_id === lastSender && delta >= 0 && delta < GROUP_WINDOW_MS);
    out.push({
      kind: 'msg',
      m,
      mine: !!(myId && m.sender_id === myId),
      grouped,
      time: timeLabel(d),
      fullTime: d ? d.toLocaleString() : '',
    });
    lastSender = m.sender_id;
    lastTime = d;
  }
  return out;
}

export default function DiscussThread({ subjectType, subjectId, subjectTitle, threadId, onRead, shareUrl }) {
  const { user } = useAuth();
  const myId = user?.id || null;
  const [thread, setThread]   = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [body, setBody]       = useState('');
  const [sending, setSending] = useState(false);
  const [hasNew, setHasNew]   = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [liveMsg, setLiveMsg] = useState(''); // sr-only announcement for incoming realtime messages
  const [pickerFor, setPickerFor] = useState(null); // message id whose reaction picker is open
  const [typers, setTypers] = useState({}); // userId -> display name currently typing
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareChannels, setShareChannels] = useState([]);
  const [shareMsg, setShareMsg] = useState('');
  const canShare = !!shareUrl && !!subjectId;
  const typingTimersRef = useRef({});
  const lastTypingSentRef = useRef(0);

  const scrollRef = useRef(null);
  const pinnedRef = useRef(true);          // is the user scrolled to the bottom?
  const prependAnchorRef = useRef(null);   // { prevHeight, prevTop } armed right before a prepend commit
  const lastNewestRef = useRef(null);      // newest message id the append effect last handled
  const myIdRef = useRef(myId);
  myIdRef.current = myId;                   // keep fresh for the realtime closure
  const onReadRef = useRef(onRead);
  onReadRef.current = onRead;
  const messagesRef = useRef([]);
  messagesRef.current = messages; // fresh for realtime closures (typing name resolution)

  // Best-effort read cursor: mark the thread read (clears its unread badge).
  async function markRead(tid, messageId) {
    if (!tid) return;
    try {
      await api.post(`/messenger/threads/${tid}/read`, messageId ? { messageId } : {});
      onReadRef.current?.();
    } catch { /* best effort */ }
  }

  // One sorted view drives rendering, the newest/oldest cursors, and grouping.
  const ordered = useMemo(() => [...messages].sort(byCreatedAtId), [messages]);
  const decorated = useMemo(() => decorate(ordered, myId), [ordered, myId]);
  const newestId = ordered.length ? ordered[ordered.length - 1].id : null;

  // Resolve the thread (a channel/DM passes threadId directly; an entity thread
  // is get-or-created by subject), then load its newest page. Re-runs when the
  // active conversation changes (switching channels).
  useEffect(() => {
    let alive = true;
    async function boot() {
      setLoading(true);
      setError(null);
      setMessages([]);
      setHasMore(false);
      // Reset per-conversation UI so a draft/typing/pickers can't bleed across
      // subjects when this component is reused in place (unkeyed entity mounts).
      setBody('');
      setTypers({});
      setPickerFor(null);
      setHasNew(false);
      setLiveMsg('');
      // Share control is per-subject too — else deal B shows deal A's "Shared to
      // #general" confirmation / an open channel select the user never triggered.
      setShareOpen(false);
      setShareChannels([]);
      setShareMsg('');
      lastNewestRef.current = null;
      try {
        const t = threadId
          ? { id: threadId }
          : unwrap(await api.post('/messenger/threads', { subjectType, subjectId, subjectTitle }))?.thread;
        if (!alive || !t) return;
        setThread(t);
        const res = unwrap(await api.get(`/messenger/threads/${t.id}/messages`)) || {};
        if (!alive) return;
        pinnedRef.current = true; // land at the newest message on first load
        const loaded = res.items || [];
        setMessages(loaded);
        setHasMore(!!res.hasMore);
        markRead(t.id, loaded[loaded.length - 1]?.id); // opening a conversation clears its unread badge
      } catch (e) {
        if (alive) setError(e.response?.data?.message || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    }
    boot();
    return () => { alive = false; };
  }, [subjectType, subjectId, subjectTitle, threadId]);

  // Realtime: join `thread:<id>` once the thread exists and append live posts.
  useRealtime({
    type: 'thread',
    id: thread?.id,
    events: {
      'message.new': (msg) => {
        setMessages((prev) => appendUnique(prev, msg));
        // Announce only genuinely-incoming messages (not our own echo). The list
        // itself is aria-live=off, so initial load and load-older don't burst.
        if (msg && msg.sender_id && msg.sender_id !== myIdRef.current) {
          setLiveMsg(`${msg.sender_name || 'Someone'}: ${String(msg.body || '').slice(0, 120)}`);
          markRead(msg.thread_id, msg.id); // stay caught up while the thread is open
        }
      },
      'reaction.changed': (p) => applyReactions(p.messageId, p.reactions),
      typing: (p) => {
        if (!p || !p.userId || p.userId === myIdRef.current) return;
        const name = (messagesRef.current.find((m) => m.sender_id === p.userId) || {}).sender_name || 'Someone';
        setTypers((prev) => ({ ...prev, [p.userId]: name }));
        clearTimeout(typingTimersRef.current[p.userId]);
        typingTimersRef.current[p.userId] = setTimeout(() => {
          setTypers((prev) => { const n = { ...prev }; delete n[p.userId]; return n; });
        }, 3500);
      },
    },
  });

  // Clear any pending typing timers on unmount.
  useEffect(() => () => { Object.values(typingTimersRef.current).forEach(clearTimeout); }, []);

  // Throttled typing signal while composing (at most once per 2s).
  const myName = user?.name || 'Someone';
  function onComposeChange(v) {
    setBody(v);
    const now = Date.now();
    if (thread?.id && v && now - lastTypingSentRef.current > 2000) {
      lastTypingSentRef.current = now;
      emitTyping(thread.id, myName);
    }
  }

  function applyReactions(messageId, reactions) {
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, reactions } : m)));
  }
  async function toggleReaction(messageId, emoji) {
    setPickerFor(null);
    try {
      const res = unwrap(await api.post(`/messenger/messages/${messageId}/reactions`, { emoji }));
      if (res) applyReactions(messageId, res.reactions);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  }

  // Bottom-append handling: react only when the NEWEST message id changes (a
  // realtime/sent/initial message). Load-older prepends don't change the newest
  // id, so they never pin-to-bottom or raise the "new messages" pill.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || newestId === null) return;
    if (newestId === lastNewestRef.current) return;
    lastNewestRef.current = newestId;
    if (pinnedRef.current) {
      el.scrollTop = el.scrollHeight;
      setHasNew(false);
    } else {
      setHasNew(true);
    }
  }, [newestId]);

  // After a load-older prepend commits, restore the viewport so the user stays
  // anchored on the same message (the older page was inserted above the fold).
  // Runs synchronously before paint, so there is no visible jump.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const anchor = prependAnchorRef.current;
    if (!el || !anchor) return;
    prependAnchorRef.current = null;
    el.scrollTop = el.scrollHeight - anchor.prevHeight + anchor.prevTop;
  }, [ordered]);

  // Fetch the page just before the oldest message we hold and prepend it.
  async function loadOlder() {
    if (!thread || loadingOlder || !hasMore) return;
    const oldest = ordered[0];
    if (!oldest) return;
    setLoadingOlder(true);
    setError(null);
    try {
      const res = unwrap(await api.get(`/messenger/threads/${thread.id}/messages`, {
        params: { before: oldest.id, limit: PAGE_SIZE },
      })) || {};
      const older = res.items || [];
      const seen = new Set(messages.map((m) => m.id));
      const fresh = older.filter((m) => !seen.has(m.id));
      if (fresh.length) {
        const el = scrollRef.current;
        // Arm the scroll anchor immediately before the prepend commit — capturing
        // it HERE (not before the fetch) means an in-flight realtime append can't
        // consume it. The useLayoutEffect above restores scroll from it.
        prependAnchorRef.current = el ? { prevHeight: el.scrollHeight, prevTop: el.scrollTop } : null;
        setMessages((prev) => {
          const seen2 = new Set(prev.map((m) => m.id));
          const fresh2 = older.filter((m) => !seen2.has(m.id));
          return fresh2.length ? [...fresh2, ...prev] : prev;
        });
      }
      setHasMore(!!res.hasMore);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoadingOlder(false);
    }
  }

  function onListScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    pinnedRef.current = atBottom;
    if (atBottom) setHasNew(false);
  }

  function scrollToBottom() {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    pinnedRef.current = true;
    setHasNew(false);
  }

  async function send() {
    const text = body.trim();
    if (!text || !thread || sending) return;
    setSending(true);
    setError(null);
    try {
      const msg = unwrap(await api.post(`/messenger/threads/${thread.id}/messages`, { body: text }))?.message;
      setBody('');
      // Optimistic append; the realtime echo dedupes against this by id. Always
      // pin to bottom for the author's own message.
      if (msg) {
        pinnedRef.current = true;
        setMessages((prev) => appendUnique(prev, msg));
      }
      // Force the author's own message into view on the next frame regardless of
      // whether the realtime echo already appended the row (dedup → no re-render).
      requestAnimationFrame(() => scrollToBottom());
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSending(false);
    }
  }

  // Share this object as a card into a channel the user belongs to.
  async function openShare() {
    setShareMsg('');
    try {
      setShareChannels(unwrap(await api.get('/messenger/channels'))?.items || []);
      setShareOpen(true);
    } catch (e) { setError(e.response?.data?.message || e.message); }
  }
  async function shareToChannel(channelId) {
    setShareOpen(false);
    try {
      const data = unwrap(await api.get(`/messenger/channels/${channelId}`));
      await api.post(`/messenger/threads/${data.thread.id}/messages`, {
        entity: { type: subjectType, id: subjectId, label: subjectTitle, url: shareUrl },
      });
      setShareMsg(`Shared to #${data.channel.name || 'channel'}`);
    } catch (e) { setError(e.response?.data?.message || e.message); }
  }

  // Download an attachment through the authenticated messenger route (the raw
  // /uploads url is never exposed), as a blob so the bearer token is sent.
  async function downloadAttachment(messageId, name) {
    try {
      const res = await api.get(`/messenger/messages/${messageId}/attachment`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.message || 'No access to that attachment');
    }
  }

  // Upload a file to DMS, then post a message carrying that document.
  async function attachFile(file) {
    if (!file || !thread || uploading) return;
    setUploading(true);
    setError(null);
    try {
      const res = await dmsApi.upload(file, { name: file.name });
      const documentId = res?.document?.id;
      if (!documentId) return;
      const msg = unwrap(await api.post(`/messenger/threads/${thread.id}/messages`, { body: body.trim() || undefined, documentId }))?.message;
      setBody('');
      if (msg) { pinnedRef.current = true; setMessages((prev) => appendUnique(prev, msg)); }
      requestAnimationFrame(() => scrollToBottom());
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="messenger-thread" data-testid="messenger-thread" data-loading={loading ? 'true' : 'false'}>
      {error && <p className="messenger-error" role="alert">{error}</p>}

      {canShare && (
        <div className="messenger-share">
          <button type="button" className="messenger-share-btn" data-testid="messenger-share" onClick={openShare}>
            {/* subject types are snake_case on the wire ('sourcing_event') — humanize
                so the button doesn't read "Share this sourcing_event to a channel…" */}
            Share this {String(subjectType || 'item').replace(/_/g, ' ')} to a channel…
          </button>
          {shareOpen && (
            <select
              data-testid="messenger-share-select"
              defaultValue=""
              onChange={(e) => { if (e.target.value) shareToChannel(e.target.value); }}
            >
              <option value="" disabled>Pick a channel…</option>
              {shareChannels.map((c) => <option key={c.id} value={c.id}># {c.name}</option>)}
            </select>
          )}
          {shareMsg && <span className="messenger-share-msg" data-testid="messenger-share-msg">{shareMsg}</span>}
        </div>
      )}

      {/* Screen-reader announcer for incoming realtime messages only — the list
          below is aria-live=off so initial load / load-older don't burst-announce. */}
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{ position: 'absolute', width: 1, height: 1, margin: -1, padding: 0, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 }}
      >
        {liveMsg}
      </div>

      <div
        ref={scrollRef}
        onScroll={onListScroll}
        className="messenger-list"
        role="log"
        aria-live="off"
        aria-label="Messages"
      >
        {loading ? (
          <p className="messenger-placeholder">Loading…</p>
        ) : !messages.length ? (
          <div className="messenger-empty" data-testid="messenger-empty">
            <div className="messenger-empty-icon" aria-hidden="true">💬</div>
            <p className="messenger-empty-title">Start the conversation</p>
            <p className="messenger-empty-sub">
              {subjectTitle
                ? `Be the first to post on ${subjectTitle}. @mention a teammate to loop them in.`
                : 'Be the first to post. @mention a teammate to loop them in.'}
            </p>
          </div>
        ) : (
          <>
            {hasMore && (
              <button
                type="button"
                className="messenger-loadolder"
                data-testid="messenger-load-older"
                onClick={loadOlder}
                disabled={loadingOlder}
              >
                {loadingOlder ? 'Loading…' : 'Load older messages'}
              </button>
            )}
            {decorated.map((item) =>
              item.kind === 'day' ? (
                <div key={item.key} className="messenger-day" role="separator" aria-label={item.label}>
                  {item.label}
                </div>
              ) : (
                <div
                  key={item.m.id}
                  data-testid="messenger-message-row"
                  title={item.grouped ? item.fullTime : undefined}
                  className={`messenger-row${item.mine ? ' mine' : ''}${item.grouped ? ' grouped' : ''}`}
                >
                  <div className="messenger-gutter" aria-hidden="true">
                    {item.grouped
                      ? <span className="messenger-grouptime">{item.time}</span>
                      : <span className="avatar">{initial(item.m.sender_name)}</span>}
                  </div>
                  <div className="messenger-content">
                    {!item.grouped && (
                      <div className="messenger-meta">
                        <span className="messenger-name">{item.m.sender_name || 'Unknown'}</span>
                        {item.mine && <span className="messenger-you">you</span>}
                        <span className="messenger-time" title={item.fullTime}>{item.time}</span>
                      </div>
                    )}
                    <div className="messenger-body"><MessageBody body={item.m.body} /></div>
                    {item.m.attachment && (
                      <button
                        type="button"
                        className="messenger-attachment"
                        data-testid="messenger-attachment"
                        onClick={() => downloadAttachment(item.m.id, item.m.attachment.name)}
                      >
                        📎 {item.m.attachment.name || 'attachment'}
                      </button>
                    )}
                    {item.m.entity && (
                      <a className="messenger-entity-card" data-testid="messenger-entity-card" href={safeHref(item.m.entity.url)}>
                        <span className="messenger-entity-type">{item.m.entity.type}</span>
                        <span className="messenger-entity-label">{item.m.entity.label || item.m.entity.id}</span>
                      </a>
                    )}
                    <div className="messenger-reactions">
                      {(item.m.reactions || []).map((r) => {
                        const mine = !!(r.userIds && r.userIds.includes(myId));
                        return (
                          <button
                            key={r.emoji}
                            type="button"
                            data-testid="messenger-reaction"
                            className={`messenger-reaction${mine ? ' mine' : ''}`}
                            // `mine` is conveyed visually by the class; aria-pressed is
                            // what tells a screen-reader user they already reacted.
                            aria-pressed={mine}
                            aria-label={`${r.emoji} ${r.count}${mine ? ', you reacted' : ''}`}
                            onClick={() => toggleReaction(item.m.id, r.emoji)}
                          >
                            <span>{r.emoji}</span> <span className="messenger-reaction-count">{r.count}</span>
                          </button>
                        );
                      })}
                      <span className="messenger-react-add">
                        <button
                          type="button"
                          className="messenger-react-btn"
                          data-testid="messenger-react-add"
                          aria-label="Add reaction"
                          onClick={() => setPickerFor(pickerFor === item.m.id ? null : item.m.id)}
                        >
                          ☺
                        </button>
                        {pickerFor === item.m.id && (
                          <span className="messenger-react-picker" role="menu">
                            {COMMON_EMOJI.map((e) => (
                              <button key={e} type="button" role="menuitem" onClick={() => toggleReaction(item.m.id, e)}>{e}</button>
                            ))}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )
            )}
          </>
        )}
      </div>

      {hasNew && (
        <button
          type="button"
          data-testid="messenger-new-messages"
          onClick={scrollToBottom}
          className="messenger-newmsg"
        >
          ↓ New messages
        </button>
      )}

      {Object.keys(typers).length > 0 && (
        <div className="messenger-typing" data-testid="messenger-typing" aria-live="polite">
          {Object.values(typers).join(', ')} {Object.keys(typers).length > 1 ? 'are' : 'is'} typing…
        </div>
      )}

      <div className="messenger-composer">
        <input
          ref={fileRef}
          type="file"
          style={{ display: 'none' }}
          data-testid="messenger-file-input"
          onChange={(e) => { attachFile(e.target.files?.[0]); e.target.value = ''; }}
        />
        <button
          type="button"
          className="messenger-attach-btn"
          data-testid="messenger-attach"
          onClick={() => fileRef.current?.click()}
          disabled={!thread || uploading}
          aria-label="Attach a file"
          title="Attach a file"
        >
          {uploading ? '…' : '📎'}
        </button>
        <MentionTextarea
          value={body}
          onChange={onComposeChange}
          onSubmit={send}
          disabled={!thread || sending}
          placeholder="Write a message… (Enter to send, @ to mention)"
        />
        <button
          className="messenger-send"
          data-testid="messenger-send"
          onClick={send}
          disabled={!thread || sending || !body.trim()}
        >
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
}
